/**
 * accountingDocuments.js — Muhasebe FAZ 6 "Belgeler", blob indirme hook'ları.
 *
 * Hooks:
 *   useDownloadInvoicePdf()   — GET /admin/invoices/{invoice_id}/pdf
 *   useDownloadStatementPdf() — GET /admin/students/{user_id}/accounting/ekstre-pdf
 *   useDownloadNoticePdf()    — GET /admin/legal-cases/{case_id}/notice-pdf
 *   useLegalCasesForNotice()  — GET /admin/legal-cases?user_id={user_id} (İhtar
 *                                akışının 0/1/>1 dosya dallanması bu sorgunun
 *                                sonucuna göre çağıran component'te yapılır —
 *                                bu hook yalnızca listeyi döner, karar vermez.)
 *
 * Üçü de blob indirir: `responseType: 'blob'`, `Content-Disposition`'dan dosya adı çıkar, yoksa
 * varsayılan üret, `URL.createObjectURL` + geçici `<a download>` + `revokeObjectURL`.
 *
 * Üç uç da `Authorization: Bearer` gerektirir (`useAuth().authHeaders()`) —
 * `apiClient`'ın kendi interceptor'ı zaten header'ı ekliyor olsa da diğer
 * çağıran yüzeyler (AdminInvoices.jsx, AdminLegal.jsx) `authHeaders()`'ı
 * açıkça geçiyor; burada da AYNI desen sürdürüldü.
 *
 * NOT: bu üç hook `useMutation` ile sarmalandı (dosya konumu `api/mutations/`
 * ile tutarlı) ama çağıran component'ler PLAN'ın istediği gibi KENDİ local
 * `useState(false)` loading bayrağını taşır (çift tıklama koruması,
 * AdminInvoices.jsx:69,96 deseni) — `mutation.isPending` ayrıca kullanılmaz.
 */

import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';

/** Content-Disposition header'ından dosya adını çıkarır, yoksa fallback döner. */
function filenameFromDisposition(response, fallback) {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename\*?=["']?(?:UTF-8'')?([^"';\n]+)["']?/i);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** Blob'u tarayıcıya indirir. */
function triggerBlobDownload(blobData, filename) {
  const url = window.URL.createObjectURL(new Blob([blobData]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// ── useDownloadInvoicePdf ────────────────────────────────────────────────────

/** `mutateAsync(invoiceId)` — fatura PDF'ini indirir. */
export function useDownloadInvoicePdf() {
  const { authHeaders } = useAuth();

  return useMutation({
    mutationFn: async (invoiceId) => {
      const response = await apiClient.get(`/admin/invoices/${invoiceId}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const filename = filenameFromDisposition(response, `${invoiceId}.pdf`);
      triggerBlobDownload(response.data, filename);
      return { filename };
    },
  });
}

// ── useDownloadStatementPdf ──────────────────────────────────────────────────

/** `mutateAsync(userId)` — öğrencinin cari hesap dökümü PDF'ini indirir. */
export function useDownloadStatementPdf() {
  const { authHeaders } = useAuth();

  return useMutation({
    mutationFn: async (userId) => {
      const response = await apiClient.get(`/admin/students/${userId}/accounting/ekstre-pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const filename = filenameFromDisposition(response, `cari-ekstre_${userId}.pdf`);
      triggerBlobDownload(response.data, filename);
      return { filename };
    },
  });
}

// ── useDownloadNoticePdf ─────────────────────────────────────────────────────

/** `mutateAsync(caseId)` — bir icra dosyasına bağlı ihtarname PDF'ini indirir. */
export function useDownloadNoticePdf() {
  const { authHeaders } = useAuth();

  return useMutation({
    mutationFn: async (caseId) => {
      const response = await apiClient.get(`/admin/legal-cases/${caseId}/notice-pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const filename = filenameFromDisposition(response, `ihtar_${caseId}.pdf`);
      triggerBlobDownload(response.data, filename);
      return { filename };
    },
  });
}

// ── useLegalCasesForNotice ───────────────────────────────────────────────────

/**
 * `mutateAsync(userId)` — öğrencinin icra dosyalarını döner (`legal_cases[]`).
 * Otomatik (mount'ta) çalışan bir `useQuery` DEĞİL — İhtar butonuna tıklanınca
 * çağrılır (S-5: dosya yoksa PDF isteği hiç atılmaz, karar çağıran component'te).
 */
export function useLegalCasesForNotice() {
  const { authHeaders } = useAuth();

  return useMutation({
    mutationFn: async (userId) => {
      const response = await apiClient.get('/admin/legal-cases', {
        headers: authHeaders(),
        params: { user_id: userId, limit: 500 },
      });
      return response.data?.legal_cases || [];
    },
  });
}
